import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import {
  Checkbox,
  HiddenLabel,
  HiddenLegend,
  IconButton,
  Label,
  PopoverOutlet,
  PopoverPrimitive,
  SegmentedControl,
  Select,
  Slider,
  Text,
  Textarea,
  type PopoverPrimitiveOptions,
} from '@figma/fpl-components';
import { Icon24Adjust } from '@figma/fpl-icons';
import { ColorTheme } from '@figma/fpl-themes';
import { borderRadius, color, shadow, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { I18N_KEYS } from '@/language/keys';
import { FormattedNumberInput } from '@/UI/NumberInput/FormattedNumberInput';
import { NumberInputFormatter } from '@/UI/NumberInput/numberInputFormatter';
import { isGenerateSeed, type GenerateParam, type GenerateParamValue, type GenerateSeed } from '@/api/generate.api';
import { optionLabel } from './optionLabel';
import { SLIDER_THUMB_STYLE, SliderField } from './SliderField';

type ParamChange = (key: string, value: GenerateParamValue) => void;

/**
 * The Adjust button and the More Settings panel it opens: one row per setting the footer's menus leave out,
 * each with the control for its type. A setting shows its default until it is changed.
 */
export function MoreSettings({
  params,
  values,
  placement,
  onChange,
}: {
  params: readonly GenerateParam[];
  values: Readonly<Record<string, GenerateParamValue>>;
  placement: PopoverPrimitiveOptions['placement'];
  onChange: ParamChange;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { getTriggerProps, getContainerProps } = PopoverPrimitive.usePopover({
    isOpen: open,
    onOpenChange: setOpen,
    type: 'dialog',
    softDismiss: true,
    placement,
    offset: 4,
  });
  const label = t(I18N_KEYS.GENERATE_PAGE.MORE_SETTINGS);

  return (
    <>
      <IconButton aria-label={label} {...getTriggerProps()}>
        <Icon24Adjust {...stylex.props(styles.triggerIcon)} />
      </IconButton>
      <PopoverPrimitive.Container {...getContainerProps({ 'aria-label': label })}>
        <ColorTheme brand="weave" mode="dark">
          {/* Keeps a Select menu inside the panel, above it rather than behind it. */}
          <PopoverOutlet>
            <div {...stylex.props(styles.panel)}>
              {params.map((param, index) => (
                <Fragment key={param.key}>
                  {index > 0 && <div {...stylex.props(styles.divider)} />}
                  <div {...stylex.props(styles.row)}>
                    <Text.p variant="body-medium" color="secondary" {...stylex.props(styles.title)}>
                      {param.title}
                    </Text.p>
                    <SettingControl param={param} value={values[param.key]} onChange={onChange} />
                  </div>
                </Fragment>
              ))}
            </div>
          </PopoverOutlet>
        </ColorTheme>
      </PopoverPrimitive.Container>
    </>
  );
}

function SettingControl({
  param,
  value,
  onChange,
}: {
  param: GenerateParam;
  value: GenerateParamValue | undefined;
  onChange: ParamChange;
}) {
  switch (param.type) {
    case 'enum':
      return (
        <EnumSetting param={param} value={typeof value === 'string' ? value : param.defaultValue} onChange={onChange} />
      );
    case 'boolean':
      return (
        <BooleanSetting
          param={param}
          value={typeof value === 'boolean' ? value : param.defaultValue}
          onChange={onChange}
        />
      );
    case 'integer':
    case 'float':
      return (
        <RangeSetting
          param={param}
          value={typeof value === 'number' ? value : param.defaultValue}
          onChange={onChange}
        />
      );
    case 'number':
      return (
        <NumberSetting
          param={param}
          value={typeof value === 'number' ? value : param.defaultValue}
          onChange={onChange}
        />
      );
    case 'seed':
      return (
        <SeedSetting param={param} value={isGenerateSeed(value) ? value : param.defaultValue} onChange={onChange} />
      );
    case 'string':
      return (
        <Textarea
          aria-label={param.title}
          value={typeof value === 'string' ? value : param.defaultValue}
          onChange={(next) => onChange(param.key, next)}
          rows={3}
        />
      );
    default:
      return null;
  }
}

function EnumSetting({
  param,
  value,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'enum' }>;
  value: string;
  onChange: ParamChange;
}) {
  const handleChange = useCallback(
    (next: string | undefined) => {
      if (next !== undefined) onChange(param.key, next);
    },
    [onChange, param.key],
  );
  return (
    <Select.Root value={value} onChange={handleChange}>
      <Select.Trigger label={<HiddenLabel>{param.title}</HiddenLabel>} width="fill" />
      <Select.Container>
        {param.options.map((option) => (
          <Select.Option key={option} value={option} title={optionLabel(param.key, option)}>
            {optionLabel(param.key, option)}
          </Select.Option>
        ))}
      </Select.Container>
    </Select.Root>
  );
}

function BooleanSetting({
  param,
  value,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'boolean' }>;
  value: boolean;
  onChange: ParamChange;
}) {
  const { t } = useTranslation();
  const handleChange = useCallback((next: string) => onChange(param.key, next === 'enable'), [onChange, param.key]);
  return (
    <SegmentedControl.Root
      legend={<HiddenLegend>{param.title}</HiddenLegend>}
      value={value ? 'enable' : 'disable'}
      onChange={handleChange}
    >
      <SegmentedControl.TextOption value="enable" label={t(I18N_KEYS.GENERATE_PAGE.ENABLE)} />
      <SegmentedControl.TextOption value="disable" label={t(I18N_KEYS.GENERATE_PAGE.DISABLE)} />
    </SegmentedControl.Root>
  );
}

function RangeSetting({
  param,
  value,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'integer' | 'float' }>;
  value: number;
  onChange: ParamChange;
}) {
  const integer = param.type === 'integer';
  const handleChange = useCallback(
    (next: number) => onChange(param.key, integer ? Math.round(next) : next),
    [integer, onChange, param.key],
  );
  return (
    <SliderField
      label={param.title}
      value={value}
      min={param.min}
      max={param.max}
      step={integer ? 1 : 0.01}
      bigStep={integer ? 10 : (param.max - param.min) / 10}
      decimals={integer ? 0 : 2}
      onChange={handleChange}
    />
  );
}

function NumberSetting({
  param,
  value,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'number' }>;
  value: number;
  onChange: ParamChange;
}) {
  const formatter = useMemo(() => new NumberInputFormatter({ decimals: param.decimals }), [param.decimals]);
  const handleChange = useCallback((next: number) => onChange(param.key, next), [onChange, param.key]);
  return (
    <FormattedNumberInput
      value={value}
      onSubmit={handleChange}
      formatter={formatter}
      ariaLabel={param.title}
      disabled={false}
      scrubbable={false}
    />
  );
}

/** The server draws a random seed between 1 and this, so the slider covers that range. */
const SEED_SLIDER_MAX = 1_000_000;
/** As the canvas Seed field, a typed seed may go past the slider's range. */
const SEED_FORMATTER = new NumberInputFormatter({ decimals: 0, min: 0, max: Number.MAX_SAFE_INTEGER });

/** A fixed seed, typed or picked on the slider, or Random for a fresh seed on every run. */
function SeedSetting({
  param,
  value,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'seed' }>;
  value: GenerateSeed;
  onChange: ParamChange;
}) {
  const { t } = useTranslation();
  const handleSeedChange = useCallback(
    (seed: number) => onChange(param.key, { ...value, seed: Math.round(seed) }),
    [onChange, param.key, value],
  );
  const handleRandomChange = useCallback(
    (isRandom: boolean) => onChange(param.key, { ...value, isRandom }),
    [onChange, param.key, value],
  );

  return (
    <div {...stylex.props(styles.seed)}>
      <div {...stylex.props(styles.seedHeader)}>
        <FormattedNumberInput
          value={value.seed}
          onSubmit={handleSeedChange}
          formatter={SEED_FORMATTER}
          ariaLabel={param.title}
          disabled={value.isRandom}
          width="64px"
          scrubbable={false}
        />
        <Checkbox
          label={
            <Label className={stylex.props(styles.randomLabel).className}>
              {t(I18N_KEYS.GENERATE_PAGE.RANDOM_SEED)}
            </Label>
          }
          checked={value.isRandom}
          onChange={handleRandomChange}
        />
      </div>
      <Slider
        style={SLIDER_THUMB_STYLE}
        size="md"
        value={Math.min(SEED_SLIDER_MAX, value.seed)}
        min={0}
        max={SEED_SLIDER_MAX}
        step={1}
        bigStep={SEED_SLIDER_MAX / 10}
        rangeAnchor={0}
        aria-label={param.title}
        disabled={value.isRandom}
        onChange={handleSeedChange}
      />
    </div>
  );
}

const styles = stylex.create({
  // Secondary like the other settings in the row. The button sets the icon color, so it is set on the icon.
  triggerIcon: {
    '--fpl-icon-color': color.iconSecondary,
  },
  panel: {
    backgroundColor: color.bgElevated,
    borderRadius: borderRadius.large,
    boxShadow: shadow.elevation500,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer25,
    maxHeight: '70vh',
    overflowY: 'auto',
    paddingBottom: spacer.spacer3,
    paddingTop: spacer.spacer2,
    width: '241px',
  },
  row: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: spacer.spacer2,
    paddingBlock: spacer.spacer1,
    paddingInline: spacer.spacer3,
  },
  title: {
    margin: 0,
  },
  divider: {
    backgroundColor: color.conditionalborder,
    flexShrink: 0,
    height: '1px',
  },
  seed: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer2,
  },
  seedHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  randomLabel: {
    color: color.textTertiary,
  },
});
